import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from 'services/api.service';
import { Document } from 'types/document';
import { debounce } from 'lodash';

export default function useDocuments() {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [meta, setMeta] = useState<{ totalPages: number; count: number }>({
    totalPages: 0,
    count: 0,
  });

  const fetchDocuments = useMemo(
    () =>
      debounce((searchParams: URLSearchParams) => {
        setIsError(false);
        setIsLoading(true);
        const args = searchParams.toString()
          ? searchParams.toString()
          : 'page%5Bnumber%5D=1&page%5Bsize%5D=20';
        api
          .get<{
            documents: Document[];
            meta: { total_pages: number; count: number };
          }>(`/documents?${args.toString()}`)
          .then((res) => {
            setDocuments(res.data.documents || []);
            setMeta({
              totalPages: res.data.meta?.total_pages || 0,
              count: res.data.meta?.count || 0,
            });
            setIsLoading(false);
          })
          .catch((error) => {
            console.error('Documents API error:', error);
            setIsError(true);
            setIsLoading(false);
          });
      }, 500),
    []
  );

  const refetch = useCallback(() => {
    fetchDocuments(searchParams);
  }, [searchParams, fetchDocuments]);

  useEffect(() => {
    refetch();
  }, [searchParams, refetch]);

  const downloadDocument = useCallback((doc: Document) => {
    // Create a temporary link to download the document
    const link = document.createElement('a');
    link.href = doc.secureUrl || doc.url;
    link.download = `${doc.name}.${doc.fileType}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return {
    documents,
    meta,
    isLoading,
    isError,
    refetch,
    downloadDocument,
    fetchDocuments,
  };
}
